import React, {useContext, useEffect, useState} from "react";
import Typography from "@mui/material/Typography";
import SummaryShipmentTable, {Forecast,} from "./components/SummaryShipmentTable/SummaryShipmentTable";
import SummaryShipmentPlanLineChart from "./components/SummaryShipmentPlanLineChart/SummaryShipmentPlanLineChart";
import Paper from "@mui/material/Paper";
import FormControl from "@mui/material/FormControl";
import Box from "@mui/material/Box";
import ShipmentSummary from "../components/ShipmentSummary/ShipmentSummary";
import {calcPreviousMonthsISO, formatToMonthNameYear, MONTH_INITIALS} from "./utils/date.utils";
import Select from "../components/Select/Select";
import useClient from "../hooks/useClient";
import useQueryParams, {CHANNEL_PARAM, COUNTRY_PARAM} from "../hooks/useQueryParams";
import {DEFAULT_CHECKS, getSetValues,} from "../components/LeftNav/LeftNavFilters";
import {DueDateContext} from "../contexts/DueDateContext";
import StyledSpinner from "../components/StyledSpinnner/StyledSpinner";

type ForecastResponse = {
  curr_sum: string;
  date: string;
  prev_sum: string;
  season: string;
  var: string;
  varPercent: string;
};

type ForecastResponseWithPriceWrapper = {
    UNT: Array<ForecastResponse>;
    AMT: Array<ForecastResponse>;
};

const MINIMUM_MONTHS = 18;
const PREVIOUS_FORECAST = "prevForecast";
const CURRENT_FORECAST = "currForecast";

let is3PW = "inline";

const LandingPage = () => {
  const { currentFC, shipmentMonthDate, windowStatus, isSubWindowLoading } = useContext(DueDateContext);
  const { fetchWithAuth, role } = useClient();
  const { search, queryParams, setParameter } = useQueryParams();
  const previousFCParam = decodeURI(queryParams.get(PREVIOUS_FORECAST) ?? "");
  const shouldUpdate =
    queryParams.get(COUNTRY_PARAM) !== null &&
    queryParams.get(COUNTRY_PARAM) !== "";
  const [previousFC, setPreviousFC] = React.useState<string>("");
  const [previousMonthsList, setPreviousMonthsList] = React.useState<string[]>([]);
  const [currentMonthsList, setCurrentMonthsList] = React.useState<string[]>([]);
  const [forecastResponses, setForecastResponses] = useState<{AMT: Forecast[], UNT: Forecast[]}>(
      {AMT: [], UNT: []},
  );
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  useEffect(() => {
    setIsInitialLoad(false);
  }, []);
  useEffect(() => {
    if (search.length && !isSubWindowLoading) {
      const callBackend = async () => {
        const lastSevenMonths = calcPreviousMonthsISO(shipmentMonthDate, 7);
        // While window is OPEN we're using month before the forecast month as currFC
        // immediately after it's CLOSED we use forecast month as currFC
        const indexCurr = windowStatus == "OPEN" ? 1 : 0
        const indexPrev = windowStatus == "OPEN" ? 2 : 1
        const currFC = lastSevenMonths[indexCurr];
        const prevFC = previousFC == "" ? lastSevenMonths[indexPrev] : previousFC;
        setPreviousFC(prevFC);
        const currMonths = lastSevenMonths.slice(indexCurr,indexCurr + 5)
        setCurrentMonthsList(currMonths);
        const prevMonths = lastSevenMonths.slice(indexPrev,indexPrev + 5)
        setPreviousMonthsList(prevMonths);

        const isAdmin = role.toLowerCase().includes("admin");
        const URI =
          isInitialLoad &&
          !search.length &&
          !isAdmin &&
          !role.includes("Unknown")
            ? Object.keys(DEFAULT_CHECKS).reduce(
                (acc, curr: keyof typeof DEFAULT_CHECKS) =>
                  `${acc}&${curr}=${getSetValues(curr)}`,
                `/v1/landing/forecast?${CURRENT_FORECAST}=${currFC}&${PREVIOUS_FORECAST}=${prevFC}`,
              )
            : `/v1/landing/forecast?${CURRENT_FORECAST}=${currFC}&${PREVIOUS_FORECAST}=${prevFC}${
                search.length ? `&${search.replace("?", "")}` : ""
              }`;

        is3PW = URI.includes("3pw") ? "inline" : "none";

        const forecastResponses = await fetchWithAuth<ForecastResponseWithPriceWrapper>(
          URI,
          (text: string) => console.warn(text),
        );

        const processForecastResponses = (fcResponses: Array<ForecastResponse>) => {
            let data: Forecast[] = fcResponses.map((forecastResponse) => ({
                currentSummary: forecastResponse.curr_sum,
                date: forecastResponse.date,
                previousSummary: forecastResponse.prev_sum,
                variance: forecastResponse.var,
                variancePercent: forecastResponse.varPercent,
            }));

            if (data.length && data.length < MINIMUM_MONTHS) {
                const split = data[data.length - 1].date.split(" ");
                const month = split[0].substring(0, 3);
                const index = MONTH_INITIALS.findIndex((init) => init === month) + 1;

                const filler: Forecast[] = Array(MINIMUM_MONTHS - data.length)
                    .fill(0)
                    .map((_, i) => ({
                        currentSummary: "0",
                        date: `${MONTH_INITIALS[(index + i) % 12]} ${
                            index + i > 11 ? parseInt(split[1]) + 1 : split[1]
                        }`,
                        previousSummary: "0",
                        variance: "0",
                        variancePercent: "0",
                    }));
                data = data.concat(filler);
            }
            return data;
        }

        const data_unt = processForecastResponses(forecastResponses['UNT']);
        const data_amt = forecastResponses['AMT'] ? processForecastResponses(forecastResponses['AMT']) : [];

        return {
            'UNT': data_unt,
            'AMT': data_amt
        };
      };

      callBackend().then((forecastResponses) => {
        setForecastResponses(forecastResponses);
      });
      setIsInitialLoad(false);
    }
  }, [search, previousFC, currentFC, role]);

  useEffect(() => {
    if (previousFCParam.length && previousFCParam !== previousFC) {
      setPreviousFC(previousFCParam);
    }
  }, [previousFCParam]);
  const setPreviousFCSelect = (value: string) => {
    setParameter(PREVIOUS_FORECAST, value);
    setPreviousFC(value);
  };
  return shouldUpdate ? (
    <div>
      <Typography variant="h3" sx={{ color: "white" }}>
        Summary Shipment Plan
      </Typography>
      <div style={{ marginBottom: 55, marginTop: 50, display: "flex" }}>
        <Box sx={{ minWidth: 120 }}>
          <FormControl disabled sx={{ width: 300, border: "1px solid #000" }}>
            <Select
              id="current-fc-select"
              loadValue={currentMonthsList[0]}
              label="Current FC"
              onChange={() => {
                /* no op */
              }}
              menuItems={currentMonthsList.map((isoDate, i) => ({
                value: isoDate,
                name: formatToMonthNameYear(currentMonthsList[i]),
              }))}
            />
          </FormControl>
        </Box>
        <Box sx={{ minWidth: 120, marginLeft: 5 }}>
          <FormControl sx={{ width: 300, border: "1px solid #000" }}>
            <Select
              id="previous-fc-select"
              label="Previous FC"
              onChange={setPreviousFCSelect}
              loadValue={previousFC}
              menuItems={previousMonthsList.map((isoDate, i) => ({
                value: isoDate,
                name: formatToMonthNameYear(previousMonthsList[i]),
              }))}
            />
          </FormControl>
        </Box>
      </div>
      <div style={{ marginBottom: 55, marginTop: 50, color: "lightskyblue" }}>
        <Typography variant="h6" component="h6">
          Summary Shipment Plan
        </Typography>
      </div>
      <Box>
        <Paper
          elevation={5}
          style={{
            backgroundColor: "#27293d",
            color: "rgb(6, 27, 100)",
            height: 450,
            marginBottom: 60,
            marginTop: -40,
            maxWidth: "1600px",
          }}
        >
          <SummaryShipmentPlanLineChart forecasts={forecastResponses['UNT']} />
        </Paper>
      </Box>
      <div style={{ marginBottom: 35, marginTop: -40, color: "lightskyblue" }}>
        <Typography variant="h6" component="h6">
          18 MONTHS SUBMISSION - UNITS
        </Typography>
      </div>
      <div style={{ marginTop: -20 }}>
        <SummaryShipmentTable forecasts={forecastResponses['UNT']} />
      </div>

        <div style={{ display: is3PW }}>
            <div style={{ marginBottom: 35, marginTop: 25, color: "lightskyblue" }}>
                <Typography variant="h6" component="h6">
                    18 MONTHS SUBMISSION - WHLSE AMT
                </Typography>
            </div>
            <div style={{ marginTop: -20 }}>
                <SummaryShipmentTable forecasts={forecastResponses['AMT']} />
            </div>
        </div>


        {
            isSubWindowLoading ? "" :
                <ShipmentSummary submitDate={shipmentMonthDate}/>
        }
    </div>
  ) : (
    <Box position="absolute" top="50%" left="50%">
      <StyledSpinner />
    </Box>
  );
};

export default LandingPage;
